
import { useMemo } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { usePersonalityProfile } from '@/hooks/usePersonalityProfile';
import { useDevelopmentRecommendations } from '@/hooks/useDevelopmentRecommendations';
import { useCareerPaths } from '@/hooks/useCareerPaths';
import { useObjectivityModel } from '@/hooks/useObjectivityModel';
import { useProfileModel, ProfileModelType } from '@/hooks/useProfileModel';
import { useProfileOverride, ProfileOverrideType } from '@/hooks/useProfileOverride';
import { sigmaToPercentile } from '@/utils/personalityCalculations';
import { calculateBehavioralScores } from '@/utils/scoreCalculations';
import { BehavioralScores, BehavioralFactor, ChartDataPoint } from '@/types/resultsTypes';
import { TRAIT_COLORS } from '@/lib/trait-colors';

export const useResultsData = (scores: BehavioralScores, storedProfileName: string | null = null) => {
  const { t } = useLanguage();
  const { activeModel, toggleModel } = useObjectivityModel();
  const { 
    activeModel: activeProfileModel, 
    toggleModel: toggleProfileModel,
    getModelLabel: getProfileModelLabel,
    getModelDescription: getProfileModelDescription
  } = useProfileModel();

  const {
    profileOverride,
    setProfileOverride,
    clearOverride,
    getProfileOptions,
    getProfileDisplayName
  } = useProfileOverride();

  // Calculate dual objectivity scores using the enhanced calculation function
  const dualScores = calculateBehavioralScores(scores);
  
  console.log('useResultsData: Using calculated scores instead of raw scores:', {
    rawScores: scores,
    calculatedScores: dualScores
  });

  const getInsights = (score: number | null, traitKey: string): string => {
    if (score === null) return `${traitKey} data is not available for this assessment`;
    
    const percentile = sigmaToPercentile(score);
    
    if (percentile >= 80) return t(`insights.${traitKey}.veryHigh`) || `Very high ${traitKey} - natural strength in this area`;
    if (percentile >= 60) return t(`insights.${traitKey}.high`) || `High ${traitKey} - above average capability`;
    if (percentile >= 40) return t(`insights.${traitKey}.moderate`) || `Moderate ${traitKey} - balanced approach`;
    if (percentile >= 20) return t(`insights.${traitKey}.low`) || `Lower ${traitKey} - opportunity for development`;
    return t(`insights.${traitKey}.veryLow`) || `Very low ${traitKey} - significant development opportunity`;
  };

  const getObjectivityInsights = (percentile: number | null): string => {
    if (percentile === null) return 'Style assessment data is not available';
    
    if (percentile >= 80) return t('insights.objectivity.veryHigh') || 'Makes decisions based purely on facts and logical analysis';
    if (percentile >= 60) return t('insights.objectivity.high') || 'Generally objective with good analytical thinking';
    if (percentile >= 40) return t('insights.objectivity.moderate') || 'Balances logical analysis with intuitive insights';
    if (percentile >= 20) return t('insights.objectivity.low') || 'Considers emotions and relationships alongside facts';
    return t('insights.objectivity.veryLow') || 'Highly intuitive and values emotional intelligence';
  };

  // Use the calculated scores for behavioral factors (DCTOS Framework)
  const behavioralFactors: BehavioralFactor[] = useMemo(() => [
    {
      name: t('factors.drive') || 'Drive',
      key: 'dominance',
      score: dualScores.aScore,
      percentile: dualScores.aPercentile,
      description: t('factors.descriptions.drive') || 'How you approach problems and challenges',
      color: TRAIT_COLORS.dominance,
      insights: getInsights(dualScores.aScore, 'dominance')
    },
    {
      name: t('factors.connection') || 'Connection',
      key: 'extraversion',
      score: dualScores.bScore,
      percentile: dualScores.bPercentile,
      description: t('factors.descriptions.connection') || 'How you interact and influence others',
      color: TRAIT_COLORS.extraversion,
      insights: getInsights(dualScores.bScore, 'extraversion')
    },
    {
      name: t('factors.task') || 'Task',
      key: 'patience',
      score: dualScores.cScore,
      percentile: dualScores.cPercentile,
      description: t('factors.descriptions.task') || 'How you respond to pace and changes',
      color: TRAIT_COLORS.patience,
      insights: getInsights(dualScores.cScore, 'patience')
    },
    {
      name: t('factors.order') || 'Order',
      key: 'formality',
      score: dualScores.dScore,
      percentile: dualScores.dPercentile,
      description: t('factors.descriptions.order') || 'How you respond to rules and procedures',
      color: TRAIT_COLORS.formality,
      insights: getInsights(dualScores.dScore, 'formality')
    }
  ], [dualScores, t]);

  // Use calculated objectivity score with proper null handling
  const objectivityScore = dualScores.eScore;
  const objectivityPercentile = dualScores.ePercentile;

  const objectivityFactor: BehavioralFactor = useMemo(() => ({
    name: t('factors.style') || 'Style',
    key: 'objectivity',
    score: objectivityScore,
    percentile: objectivityPercentile,
    description: '',
    color: TRAIT_COLORS.objectivity,
    insights: getObjectivityInsights(objectivityPercentile)
  }), [objectivityScore, objectivityPercentile, t]);

  const lineChartData: ChartDataPoint[] = useMemo(() => [
    {
      name: 'D',
      fullName: t('factors.drive') || 'Drive',
      value: behavioralFactors[0].percentile,
      color: TRAIT_COLORS.dominance
    },
    {
      name: 'C',
      fullName: t('factors.connection') || 'Connection',
      value: behavioralFactors[1].percentile,
      color: TRAIT_COLORS.extraversion
    },
    {
      name: 'T',
      fullName: t('factors.task') || 'Task',
      value: behavioralFactors[2].percentile,
      color: TRAIT_COLORS.patience
    },
    {
      name: 'O',
      fullName: t('factors.order') || 'Order',
      value: behavioralFactors[3].percentile,
      color: TRAIT_COLORS.formality
    }
  ], [behavioralFactors, t]);

  // Use the calculated scores for personality profile and other features
  // storedProfileName ensures results page matches dashboard (DB is source of truth)
  const personalityProfile = usePersonalityProfile(scores, activeProfileModel, profileOverride, storedProfileName);
  const recommendations = useDevelopmentRecommendations(behavioralFactors, objectivityFactor, scores);
  const careerPaths = useCareerPaths(scores, objectivityScore);

  return {
    behavioralFactors,
    objectivityFactor,
    lineChartData,
    personalityProfile,
    recommendations,
    careerPaths,
    activeModel,
    toggleModel,
    dualScores,
    scores,
    activeProfileModel,
    toggleProfileModel,
    getProfileModelLabel,
    getProfileModelDescription,
    profileOverride,
    setProfileOverride,
    clearOverride,
    getProfileOptions,
    getProfileDisplayName
  };
};

